import * as React from 'react';
import { debounce } from '@bloomberg/media-ui-core';

import { TickerSecurity } from '../types';

import styles from './styles.module.css';

export interface useResizeForTickerTrackProps {
  trackRef: React.MutableRefObject<HTMLDivElement | null>;
  tickers: TickerSecurity[];
  onSliderScroll: (
    _scrollLeft: number,
    atStart: boolean,
    atEnd: boolean
  ) => void;
  setSecuritiesCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  setSnapPositions: React.Dispatch<React.SetStateAction<number[]>>;
}

const RESIZE_FREQUENCY_IN_MS = 100;
const SMALL_DESKTOP_BREAKPOINT = 1123;

// ON DESKTOP: scrolling should snap to each individual ticker
// tickers should snap to the right side of the track
const calculateDesktopSnapPositions = (track: Element, trackWidth: number) => {
  const snap = [0];

  for (let i = 1; i <= track?.children.length; i++) {
    const nextTicker = track.children[i] as HTMLElement;

    if (!nextTicker) {
      continue;
    }

    nextTicker.classList.remove(styles.scrollSnap);

    const tickerRightPos = nextTicker.offsetLeft + nextTicker.offsetWidth;
    const trackScrollLeft = Math.floor(tickerRightPos - trackWidth);

    if (trackScrollLeft > 0) {
      snap.push(trackScrollLeft);
    }
  }

  return snap;
};

// ON MOBILE: scrolling should snap the right-most ticker to the left
const addSnapClassForTablet = (track: Element, trackWidth: number) => {
  const snap = [0];
  let currentTrackPosition = 0;

  for (let i = 1; i <= track?.children.length; i++) {
    const nextTicker = track.children[i] as HTMLElement;

    if (!nextTicker) {
      continue;
    }

    const tickerRightPos = nextTicker.offsetLeft + nextTicker.offsetWidth;

    if (tickerRightPos <= currentTrackPosition + trackWidth) {
      nextTicker.classList.remove(styles.scrollSnap);

      continue;
    }

    const tickerLeftPos = nextTicker.offsetLeft;

    nextTicker.classList.add(styles.scrollSnap);

    snap.push(tickerLeftPos);
    currentTrackPosition = tickerLeftPos;
  }

  return snap;
};

export function useResizeForTickerTrack({
  trackRef,
  tickers,
  onSliderScroll,
  setSecuritiesCollapsed,
  setSnapPositions,
}: useResizeForTickerTrackProps) {
  const handleResize = () => {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      return;
    }

    const track = trackRef.current;
    const securitiesText = document.querySelector(
      "[data-component='dropdown-button'] p"
    ) as HTMLElement;
    const isDesktop = window.innerWidth >= SMALL_DESKTOP_BREAKPOINT;

    if (!track) {
      return;
    }

    const trackWidth =
      isDesktop && securitiesText?.offsetWidth > 0
        ? track.offsetWidth + securitiesText.offsetWidth
        : track.offsetWidth;

    const snap = isDesktop
      ? calculateDesktopSnapPositions(track, trackWidth)
      : addSnapClassForTablet(track, trackWidth);

    setSnapPositions(snap);
  };

  React.useEffect(() => {
    handleResize();
    onSliderScroll(0, true, false);

    if (typeof document === 'undefined' || typeof window === 'undefined') {
      return () => {};
    }

    const track = trackRef.current;
    const securitiesText = document.querySelector(
      "[data-component='dropdown-button'] p"
    ) as HTMLElement;

    const resizeListener = debounce(handleResize, RESIZE_FREQUENCY_IN_MS);
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry) {
          resizeListener();
        }
      }
    });

    const securitiesAnimationListener = () => {
      if (securitiesText) {
        setSecuritiesCollapsed(securitiesText.offsetWidth === 0);
      }
    };

    if (securitiesText) {
      securitiesText.addEventListener(
        'transitionend',
        securitiesAnimationListener
      );
    }

    if (track) {
      resizeObserver.observe(track);
    }

    return () => {
      if (track) {
        resizeObserver.unobserve(track);
      }

      if (securitiesText) {
        securitiesText.removeEventListener(
          'transitionend',
          securitiesAnimationListener
        );
      }
    };
  }, [tickers]);
}
